import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
      .slice(0, 3);
  }, [article]);

  const headings = useMemo(() => {
    if (!article) return [];
    return article.content
      .filter((s) => s.type === 'heading' && s.content)
      .map((s) => ({ level: s.level || 2, text: s.content! }));
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 md:pt-18 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="text-xl font-heading text-foreground-700 mb-2">文章未找到</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '穿搭换装指南',
    },
  };

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-18">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: t('breadcrumb.home'), path: '/' },
              { label: article.category, path: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-14 md:pb-20">
          <div className="flex flex-col lg:flex-row gap-10">
            <article className="flex-1 max-w-3xl">
              <div className="mb-8">
                <span className="inline-block bg-primary-100 text-primary-700 text-xs font-medium px-2.5 py-1 rounded-md mb-4">
                  {article.category}
                </span>
                <h1 className="text-2xl md:text-4xl font-heading text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1"><i className="ri-user-line"></i> {article.author}</span>
                  <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {article.publishDate}</span>
                  <span className="flex items-center gap-1"><i className="ri-time-line"></i> {article.readTime}</span>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  {article.tags.map((tag) => (
                    <span key={tag} className="text-xs bg-secondary-100 text-secondary-700 px-2 py-1 rounded-md">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden mb-10">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="prose-custom">
                {article.content.map((section, idx) => {
                  if (section.type === 'heading' && section.content) {
                    const Tag = `h${Math.min(section.level || 2, 6)}` as keyof JSX.IntrinsicElements;
                    return (
                      <Tag
                        key={idx}
                        id={`section-${idx}`}
                        className={`font-heading text-foreground-900 mt-10 mb-4 ${
                          section.level === 2 ? 'text-xl md:text-2xl' : 'text-lg md:text-xl'
                        }`}
                      >
                        {section.content}
                      </Tag>
                    );
                  }
                  if (section.type === 'paragraph' && section.content) {
                    return (
                      <p key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5">
                        {section.content}
                      </p>
                    );
                  }
                  if (section.type === 'list' && section.items) {
                    return (
                      <ul key={idx} className="list-disc pl-5 mb-5 space-y-2">
                        {section.items.map((item, i) => (
                          <li key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed">{item}</li>
                        ))}
                      </ul>
                    );
                  }
                  if (section.type === 'image' && section.imageUrl) {
                    return (
                      <div key={idx} className="w-full rounded-lg overflow-hidden mb-6">
                        <img src={section.imageUrl} alt={section.imageAlt || ''} className="w-full object-cover" />
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </article>

            <aside className="lg:w-72 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                {headings.length > 0 && (
                  <div className="bg-background-100/80 rounded-lg p-5 mb-6">
                    <h4 className="text-sm font-semibold text-foreground-800 mb-3 flex items-center gap-2">
                      <i className="ri-list-unordered text-primary-500"></i>
                      {t('article.toc')}
                    </h4>
                    <nav className="flex flex-col gap-2">
                      {headings.map((h, i) => (
                        <a
                          key={i}
                          href={`#section-${article.content.findIndex((s) => s.type === 'heading' && s.content === h.text)}`}
                          className="text-xs text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer leading-relaxed"
                          style={{ paddingLeft: `${(h.level - 2) * 12}px` }}
                        >
                          {h.text}
                        </a>
                      ))}
                    </nav>
                  </div>
                )}

                {relatedArticles.length > 0 && (
                  <div className="bg-background-100/80 rounded-lg p-5">
                    <h4 className="text-sm font-semibold text-foreground-800 mb-4 flex items-center gap-2">
                      <i className="ri-links-line text-accent-500"></i>
                      {t('article.related')}
                    </h4>
                    <div className="flex flex-col gap-4">
                      {relatedArticles.map((ra) => (
                        <Link
                          key={ra.id}
                          to={`/article/${ra.slug}`}
                          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                          className="group flex gap-3 cursor-pointer"
                        >
                          <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0">
                            <img src={ra.coverImage} alt={ra.title} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h5 className="text-xs font-medium text-foreground-800 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
                              {ra.title}
                            </h5>
                            <span className="text-xs text-foreground-400 mt-1 block">{ra.readTime}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}